<script context="module">
  export const TIMEOUT = 59;
  export const LAST_FATAL = 'last-fatal';
</script>

<script>

  import { onDestroy } from 'svelte';
  
  import {
    DataLoadFailure,
    DisabledError,
    JsonParseError,
  } from '~/errors';
  import ErrorDetail from './recovery/ErrorDetail.svelte';
  import { cleanCache, reloadAppRoot, unregisterSW } from './recovery/tools';
  import VersionDetail from './recovery/VersionDetail.svelte';
  import Events from '../../events';

  const [, hash] = window.location.pathname.split('/');
  const title = 'We are sorry';
  const description_default = `
    Application has encountered an error and we need to reload it.
  `;
  const description_disabled = `
    Application is not available because it has been taken offline.
  `;
  const description_recurring = `
    Application is encountering recurring error. Reload will perform 
    hard refresh.
  `;
  let error;

  let dialog_type;
  let interval;
  let timer;
  let try_auto_recover;
  let recurringIssue;
  let description;
  let debug = {};

  $: reloadIn = `Application will reload in ${timer} seconds.`;

  Events.once('error:fatal', handleError);

  function handleError ({ detail }) {
    error = detail;
    const error_str = error.toString();
    const last_fatal = localStorage.getItem(LAST_FATAL);

    if (error instanceof DisabledError) {
      dialog_type = 'info';
      description = description_disabled;
      rememberError(error_str);
      return;
    }

    recurringIssue = error && last_fatal && error_str == last_fatal;
    
    description = recurringIssue ? description_recurring : description_default;

    const is_gateway_timeout = error instanceof DataLoadFailure && error.statusCode == 504;
    const is_sw_stuck = error instanceof JsonParseError;
    try_auto_recover = is_gateway_timeout || (is_sw_stuck && recurringIssue);
    debug = {
      recurringIssue,
      is_gateway_timeout,
      is_sw_stuck,
      try_auto_recover,
      last_fatal,
      error_str
    };

    rememberError(error_str);

    // Try recover if API Gateway timeouts
    if (try_auto_recover) {
      dialog_type = 'restart';
      timer = TIMEOUT;

      interval = setInterval(() => {
        if (--timer <= 0) {
          clearInterval(interval);
          reload();
        }
      }, 1000);
    }
    else {
      dialog_type = 'info';
    }
  }

  function rememberError (error_str) {
    localStorage.setItem(LAST_FATAL, error_str);
    const error_lst = localStorage.getItem('error-list') || '';
    localStorage.setItem('error-list', `${error_lst}\n${new Date().getMilliseconds()} ${error_str}`);
  }

  async function reload () {

    console.log('reload');

    if (recurringIssue) {
      await Promise.all([
        cleanCache(hash),
        unregisterSW(),
      ]);
      localStorage.removeItem(LAST_FATAL);
    }
    
    reloadAppRoot();
  } 


  onDestroy(() => {
    clearInterval(interval);
  });
</script>

<div class="recovery" class:recovery--enabled={dialog_type}>

  {#if dialog_type}
  <section class="dialog">
    <h2>{title}</h2>
    <p class="description">{@html description}</p>
    {#if dialog_type == 'restart'}
      <p class="realod-counter" {...debug}>&raquo; {reloadIn}</p>
    {/if}
    <ul class="details">
      <VersionDetail />
      <ErrorDetail {error} />
    </ul>
    <button type="button" name="button" on:click={reload}>Reload now</button>
  </section>
  {/if}

</div>

<style media="screen">

  .recovery--enabled {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, .6);
    z-index: 1000;
  }

  .dialog {
    width: 640px;
    padding: 48px 48px 32px;
    background: #000;
    color: #fff;
  }

  h2, p {
    margin-bottom: 1em;
  }

  .description {
    font-size: 32px;
  }

  .realod-counter {
    font-size: 28px;
    font-variant-numeric: tabular-nums;
  }

  .details {
    margin-bottom: 32px;
    opacity: .6;
  }

  button {
    padding: 8px 16px;
    background: transparent;
    border: 4px solid #fff;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
  }
</style>
